import { ThemedButton } from '@/app/platform/core/layout/themes/components/ThemedButton.tsx';
import { ThemedInput } from '@/app/platform/core/layout/themes/components/ThemedInput.tsx';
import { ThemedSelect } from '@/app/platform/core/layout/themes/components/ThemedSelect.tsx';
import { ThemedTextarea } from '@/app/platform/core/layout/themes/components/ThemedTextarea.tsx';
import { FormMode } from '@/app/platform/core/common.types.ts';

import React, { useState } from 'react';

import {
  Permission,
  PermissionCreate,
  PermissionUpdate,
} from '../../permission.types.ts';

interface PermissionFormProps {
  permission?: Permission;
  mode: FormMode;
  onSubmit: (data: PermissionCreate | PermissionUpdate) => Promise<void>;
  onCancel: () => void;
}

export const PermissionForm: React.FC<PermissionFormProps> = ({
  permission,
  mode,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    name: permission?.name || '',
    description: permission?.description || '',
  });
  const [status, setStatus] = useState(permission?.status ?? 'active');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'pending', label: 'Pending' },
    { value: 'archived', label: 'Archived' },
  ];

  const isViewMode = mode === 'view';

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Permission name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await onSubmit(mode === 'create' ? formData : { ...formData, status });
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <ThemedInput
        label="Name"
        value={formData.name}
        onChange={(e) => handleChange('name', e.target.value)}
        error={errors.name}
        disabled={isViewMode}
        required
      />

      <ThemedTextarea
        label="Description"
        value={formData.description}
        onChange={(e) => handleChange('description', e.target.value)}
        disabled={isViewMode}
        rows={3}
        className="block w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
      />

      {mode !== 'create' && (
        <ThemedSelect
          label="Status"
          value={status}
          onChange={setStatus}
          options={statusOptions}
          disabled={isViewMode}
          allowEmpty={false}
        />
      )}

      {!isViewMode && (
        <div className="flex justify-end gap-3 pt-4">
          <ThemedButton type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </ThemedButton>
          <ThemedButton
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
          >
            {mode === 'create' ? 'Create Permission' : 'Update Permission'}
          </ThemedButton>
        </div>
      )}
    </form>
  );
};
